import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Typography, Button, Icon, Alert, Divider, Spin, Input, Tabs, Radio, DatePicker } from 'antd';
import { getAIMetrics, getAIHealth, getSystemOverview } from '../../api/adminApi';
import RecentRequestsTable from './components/RecentRequestsTable';
import UserAnalyticsTable from './components/UserAnalyticsTable';
import EndpointAnalytics from './components/EndpointAnalytics';
import CostBreakdown from './components/CostBreakdown';
import moment from 'moment';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const AdminDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [health, setHealth] = useState(null);
  const [system, setSystem] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [endpointFilter, setEndpointFilter] = useState('');
  const [timeRange, setTimeRange] = useState('24h');
  const [customRange, setCustomRange] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('=== Starting API calls ===');
      
      // Calculate date range based on timeRange selection
      let startDate = null;
      let endDate = null;
      const now = new Date();
      
      if (timeRange && timeRange !== 'all') {
        endDate = now.toISOString();
        
        if (timeRange === '24h') {
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
        } else if (timeRange === '7d') {
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        } else if (timeRange === '30d') {
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
        } else if (timeRange === 'custom' && customRange && customRange[0] && customRange[1]) {
          startDate = customRange[0].toISOString();
          endDate = customRange[1].toISOString();
        }
      }
      
      console.log('Calling getAIMetrics() with filters:', { limit: 500, startDate, endDate });
      // Increased limit from 100 to 500 and added date filtering
      const metricsResponse = await getAIMetrics(500, startDate, endDate);
      console.log('getAIMetrics() response:', metricsResponse);
      console.log('getAIMetrics() response type:', typeof metricsResponse);
      console.log('getAIMetrics() response keys:', metricsResponse ? Object.keys(metricsResponse) : 'null/undefined');
      
      console.log('Calling getAIHealth()...');
      const healthResponse = await getAIHealth();
      console.log('getAIHealth() response:', healthResponse);
      console.log('getAIHealth() response type:', typeof healthResponse);
      
      console.log('Calling getSystemOverview()...');
      const systemResponse = await getSystemOverview();
      console.log('getSystemOverview() response:', systemResponse);
      console.log('getSystemOverview() response type:', typeof systemResponse);
      
      console.log('=== Processing responses ===');
      
      if (metricsResponse) {
        console.log('metricsResponse exists');
        console.log('metricsResponse.success:', metricsResponse.success);
        console.log('metricsResponse.data:', metricsResponse.data);
        
        if (metricsResponse.success) {
          console.log('SUCCESS: Processing metrics data');
          // Backend returns AI service data nested under 'data' property
          const aiData = metricsResponse.data;
          console.log('AI service data:', aiData);
          console.log('Raw metrics:', aiData?.metrics);
          console.log('Raw user_analytics:', aiData?.user_analytics);
          console.log('Raw recent_requests:', aiData?.recent_requests);
          
          setMetrics(aiData?.metrics || {});
          setUserAnalytics(aiData?.user_analytics || []);
          // Normalize recent requests for table display
          const rawRecent = aiData?.recent_requests || [];
          console.log('[Monitoring] recent_requests count:', rawRecent.length);
          if (rawRecent.length) {
            console.log('[Monitoring] Sample raw recent_requests[0]:', rawRecent[0]);
            const keys = Object.keys(rawRecent[0] || {});
            console.log('[Monitoring] Sample keys:', keys);
            console.log('[Monitoring] Sample request-like fields:', {
              request_data: rawRecent[0]?.request_data,
              request_body: rawRecent[0]?.request_body,
              request: rawRecent[0]?.request,
              input: rawRecent[0]?.input,
              payload: rawRecent[0]?.payload,
              params: rawRecent[0]?.params,
              query: rawRecent[0]?.query,
              request_snippet: rawRecent[0]?.request_snippet,
            });
            console.log('[Monitoring] Sample response-like fields:', {
              response_data: rawRecent[0]?.response_data,
              response_body: rawRecent[0]?.response_body,
              response: rawRecent[0]?.response,
              output: rawRecent[0]?.output,
              result: rawRecent[0]?.result,
              response_snippet: rawRecent[0]?.response_snippet,
            });
          }
          const normalized = rawRecent.map((r) => ({
            ...r,
            // Table expects these keys when available
            response_time: r.response_time_ms || r.response_time || r.latency_ms || r.latency,
            status: r.status_code || r.status,
            user_email: r.user_email || r.user || r.email || r.username || r.user_id || (r.headers && (r.headers['x-admin-user-email'] || r.headers['x-user-email'])) || (r.meta && (r.meta.user_email || r.meta.user)),
            model: r.model || r.model_name || r.ai_model,
            tokens: r.tokens || r.tokens_used || r.total_tokens || r.token_count,
            cost: r.cost || r.cost_estimate || r.estimated_cost,
            request_data: r.request_data || r.request_snippet || r.request_body || r.request || r.input || r.payload || r.params || r.query,
            response_data: r.response_data || r.response_snippet || r.response_body || r.response || r.output || r.result,
            timestamp: r.timestamp || r.time || r.created_at || r.ts || r.ts_ms || r.request_time || r.date,
            path: r.path || r.endpoint || r.url,
            request_source: (r.request_source) || (r.headers && (r.headers['x-request-source'] || r.headers['X-Request-Source'])) || (r.meta && r.meta.request_source),
          }));
          setRecentRequests(normalized);
          if (normalized.length) {
            console.log('[Monitoring] Sample normalized[0]:', normalized[0]);
            console.log('[Monitoring] request_data typeof:', typeof normalized[0].request_data, 'response_data typeof:', typeof normalized[0].response_data);
            console.log('[Monitoring] tokens:', normalized[0].tokens, 'cost:', normalized[0].cost);
            console.log('[Monitoring] Raw tokens fields:', {
              tokens: rawRecent[0]?.tokens,
              tokens_used: rawRecent[0]?.tokens_used,
              total_tokens: rawRecent[0]?.total_tokens,
              token_count: rawRecent[0]?.token_count
            });
            console.log('[Monitoring] Raw cost fields:', {
              cost: rawRecent[0]?.cost,
              cost_estimate: rawRecent[0]?.cost_estimate,
              estimated_cost: rawRecent[0]?.estimated_cost
            });
          }
          
          console.log('State updated with:', {
            metrics: aiData?.metrics || {},
            userAnalyticsCount: (aiData?.user_analytics || []).length,
            recentRequestsCount: (aiData?.recent_requests || []).length
          });
        } else {
          console.error('FAILED: metricsResponse.success is false');
          console.log('Error details:', metricsResponse.error || 'No error details');
        }
      } else {
        console.error('FAILED: metricsResponse is null/undefined');
      }
      
      if (healthResponse) {
        console.log('Processing health response...');
        if (healthResponse.success) {
          console.log('SUCCESS: Health data processed');
          setHealth(healthResponse);
        } else {
          console.error('FAILED: healthResponse.success is false');
        }
      } else {
        console.error('FAILED: healthResponse is null/undefined');
      }
      
      if (systemResponse) {
        console.log('Processing system response...');
        if (systemResponse.success) {
          console.log('SUCCESS: System data processed');
          setSystem(systemResponse.system || {});
        } else {
          console.error('FAILED: systemResponse.success is false');
        }
      } else {
        console.error('FAILED: systemResponse is null/undefined');
      }
      
      setError(null);
      setLastUpdated(new Date());
      console.log('=== API processing complete ===');
    } catch (err) {
      console.error('=== EXCEPTION in fetchData ===');
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      console.error('Full error object:', err);
      setError('Unable to connect to monitoring service: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Refetch data when time range changes
  useEffect(() => {
    console.log('[Dashboard] Time range changed:', timeRange, 'Custom range:', customRange);
    if (timeRange) {
      console.log('[Dashboard] Triggering fetchData due to time range change');
      fetchData();
    }
  }, [timeRange, customRange]); // No auto-refresh - only manual refresh via button

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div style={{ marginTop: 16 }}>Loading AI Monitoring Dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Dashboard"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" danger onClick={fetchData}>
            Retry
          </Button>
        }
      />
    );
  }

  const aiMetrics = metrics || {};
  const isHealthy = health?.status === 'healthy';
  const nonAdminLogs = recentRequests.filter(r => (r.request_source || '').toString().toLowerCase() !== 'popin-admin-dashboard');
  const displayedRecent = (endpointFilter ? nonAdminLogs.filter(r => (r.path || '').toLowerCase().includes(endpointFilter.toLowerCase())) : nonAdminLogs);

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      {/* Compact Header with Time Range Filter */}
      <Card style={{ marginBottom: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
              <Icon type="dashboard" style={{ marginRight: 8 }} />
              AI Monitoring
            </Title>
            <Tag color={isHealthy ? 'green' : 'red'} style={{ fontSize: 13 }}>
              <Icon type={isHealthy ? 'check-circle' : 'exclamation-circle'} />
              {isHealthy ? 'Operational' : 'Issues'}
            </Tag>
            {lastUpdated && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                Updated: {lastUpdated.toLocaleTimeString()}
              </Text>
            )}
          </div>
          <Button 
            type="primary"
            onClick={fetchData}
            loading={loading}
            icon="reload"
          >
            Refresh
          </Button>
        </div>
        
        {/* Time Range Filter Inline */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Radio.Group 
            value={timeRange} 
            onChange={(e) => {
              const value = e.target.value;
              setTimeRange(value);
              if (value !== 'custom') {
                setCustomRange(null);
              }
            }}
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="24h">Last 24 Hours</Radio.Button>
            <Radio.Button value="7d">Last 7 Days</Radio.Button>
            <Radio.Button value="30d">Last 30 Days</Radio.Button>
            <Radio.Button value="custom">Custom Range</Radio.Button>
          </Radio.Group>
          
          {timeRange === 'custom' && (
            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              value={customRange}
              onChange={setCustomRange}
              size="small"
              style={{ width: 350 }}
            />
          )}
        </div>
      </Card>

      {/* Tabbed Content */}
      <Card
        style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        bodyStyle={{ padding: 0 }}
      >
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          size="large"
          tabBarStyle={{ padding: '0 24px', margin: 0 }}
        >
          <TabPane 
            tab={
              <span>
                <Icon type="dashboard" />
                Overview
              </span>
            } 
            key="overview"
          >
            <div style={{ padding: '24px' }}>
              <Row gutter={[24, 24]}>
                <Col xs={24}>
                  <CostBreakdown 
                    recentRequests={displayedRecent}
                    userAnalytics={userAnalytics}
                    loading={loading}
                  />
                </Col>
              </Row>
              
              <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                <Col xs={24}>
                  <Card
                    title={
                      <span>
                        <Icon type="api" style={{ marginRight: 8, color: '#1890ff' }} />
                        Endpoint Analytics
                      </span>
                    }
                    style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                  >
                    <EndpointAnalytics 
                      recentRequests={displayedRecent}
                      loading={loading}
                    />
                  </Card>
                </Col>
              </Row>
            </div>
          </TabPane>

          <TabPane 
            tab={
              <span>
                <Icon type="user" />
                User Analytics {userAnalytics.length > 0 && <Tag color="blue">{userAnalytics.length}</Tag>}
              </span>
            } 
            key="users"
          >
            <div style={{ padding: '24px' }}>
              {userAnalytics.length > 0 ? (
                <UserAnalyticsTable data={userAnalytics} loading={loading} />
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <Icon type="user-delete" style={{ fontSize: 64, color: '#d9d9d9', marginBottom: 24 }} />
                  <div>
                    <Title level={4} style={{ color: '#999' }}>No User Analytics Available</Title>
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      User identification may not be configured in AI service calls.
                      <br />
                      Make sure API requests include X-POPin-User-ID and X-POPin-User-Email headers.
                    </Text>
                  </div>
                </div>
              )}
            </div>
          </TabPane>

          <TabPane 
            tab={
              <span>
                <Icon type="history" />
                Recent Requests {displayedRecent.length > 0 && <Tag color="blue">{displayedRecent.length}</Tag>}
              </span>
            } 
            key="requests"
          >
            <div style={{ padding: '24px' }}>
              <RecentRequestsTable data={displayedRecent} loading={loading} />
            </div>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default AdminDashboard;
